import compact from 'lodash/compact'
import find from 'lodash/find'
import flatMap from 'lodash/flatMap'
import forEach from 'lodash/forEach'
import uniqBy from 'lodash/uniqBy'

import { GlobalAnchorInjections, InjectionAnchorInjectionType } from '@Globals/Injections/Types'

import { MenuItem, MenuItemChild } from './Menu'

// anchors follow the buy_button.before/after convention: menu.mobile.<before|after>.<root category id>
const ANCHOR_PATTERN = /^menu\.mobile\.(before|after)\.(.+)$/

type InjectedFlag = { injectedMenuGroup?: true }

export const isInjectedMenuGroup = (item: MenuItem | MenuItemChild) => Boolean((item as MenuItem & InjectedFlag).injectedMenuGroup)

const extractGroupReferences = (injections: InjectionAnchorInjectionType[], anchor: string) => {
  const references: MenuItemChild[] = []

  forEach(injections, (injection) => {
    forEach(injection.items, (item) => {
      if (item.__typename !== 'InjectionTypeAnchor' || item.anchor !== anchor) {
        return
      }

      references.push(...(item.navigationItems || []) as unknown as MenuItemChild[])
    })
  })

  return uniqBy(references, 'id')
}

const findInTree = (navigation: MenuItem[], id: MenuItemChild['id']) => {
  const children = compact(flatMap(navigation, root => root?.items || []))
  const child = find(children, item => item?.id === id)
  if (child) {
    return child
  }

  const grandChildren = compact(flatMap(children, item => item?.items || []))
  return find(grandChildren, item => item?.id === id) || null
}

export const mergeInjectedMenuGroups = (
  navigation: MenuItem[] | null | undefined,
  globalAnchorInjections: GlobalAnchorInjections | undefined,
): MenuItem[] | null | undefined => {
  if (!navigation || navigation.length === 0 || !globalAnchorInjections) {
    return navigation
  }

  const merged: MenuItem[] = [...navigation]
  const rootIds = new Set(merged.map(item => item?.id))

  forEach(globalAnchorInjections, (injections, anchor) => {
    const match = ANCHOR_PATTERN.exec(anchor)
    if (!match) {
      return
    }

    const [, position, targetId] = match
    const groups = extractGroupReferences(injections, anchor)
      .filter(reference => reference?.id && !rootIds.has(reference.id))
      // referenced categories inherit their submenu from the tree; unknown ones degrade to a plain link
      .map(reference => ({ ...(findInTree(navigation, reference.id) || reference), injectedMenuGroup: true as const }))

    if (groups.length === 0) {
      return
    }

    groups.forEach(group => rootIds.add(group.id))

    let index = merged.findIndex(item => item?.id === targetId)
    if (index === -1) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`menu injection anchor "${anchor}" does not match any root category, injecting at the top`)
      }
      index = 0
    } else if (position === 'after') {
      index += 1
    }

    merged.splice(index, 0, ...groups as unknown as MenuItem[])
  })

  return merged
}
